// Ephemeral types for Learner Portal UI - will be updated to match backend models

// Derived from NATIVE_LANGUAGE_DEFINITIONS in constants/nativeLanguages.ts
import type { NativeLanguage } from '@/constants/nativeLanguages'
export type { NativeLanguage }

export type AssessmentType = 'initial_placement' | 'progress_check'

export type ProficiencyLevel = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10

// Ephemeral - UI only for now
export type Assessment = {
  readonly id: string
  readonly learnerId: string
  readonly type: AssessmentType
  readonly proficiencyLevel: ProficiencyLevel
  readonly completedAt: Date
  readonly duration?: number // in minutes
}

// Ephemeral - UI only for now
export type LearnerProfile = {
  readonly id: string
  readonly name: string
  readonly email: string
  readonly currentProficiencyLevel?: ProficiencyLevel
  readonly lastAssessmentDate?: Date
  readonly assessmentHistory: readonly Assessment[]
  readonly hasCompletedInitialPlacement: boolean
  readonly nextProgressCheckAvailable?: Date
}

// Ephemeral - UI only for now
export type AssessmentConfig = {
  readonly type: AssessmentType
  readonly estimatedMinutes: number
  readonly instructions: string
  readonly isMandatory: boolean
  readonly cooldownDays?: number
}

// Ephemeral - UI only for now
export type AssessmentQuestion = {
  readonly id: string
  readonly text: string
  readonly options: readonly string[]
}

// Onboarding types - Ephemeral UI only
export type AgeRange = '15-18' | '19-25' | '26-29' | '30-39' | '40-49' | '50+'

export type YearsStudied = '0-1' | '1-2' | '3-5' | '5+'

export type WeeklyHours = '0-1' | '1' | '2' | '3+'

export type TimeInNorthAmerica = 'never' | '0-1' | '1-2' | '2-3' | '3-4' | '4+'

export type Industry =
  | 'accounting'
  | 'healthcare'
  | 'technology'
  | 'education'
  | 'hospitality'
  | 'retail'
  | 'construction'
  | 'manufacturing'
  | 'finance'
  | 'legal'
  | 'marketing'
  | 'other'

export type LearningGoal =
  | 'get_job'
  | 'prepare_career'
  | 'improve_current_job'
  | 'academic_purposes'
  | 'personal_development'
  | 'travel'
  | 'citizenship'
  | 'other'

// EnglishAbility now uses standard CEFR codes (A0-C2)
export type EnglishAbility = 'A0' | 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2'

export type CEFRLevel = 'A0' | 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2'

// Mapping from EnglishAbility to CEFR levels (now identical)
export const ENGLISH_ABILITY_TO_CEFR: Record<EnglishAbility, CEFRLevel> = {
  A0: 'A0',
  A1: 'A1',
  A2: 'A2',
  B1: 'B1',
  B2: 'B2',
  C1: 'C1',
  C2: 'C2',
}

// Ephemeral - UI only for now
export type OnboardingData = {
  // Getting to know you
  readonly ageRange?: AgeRange
  readonly yearsStudied?: YearsStudied
  readonly weeklyHours?: WeeklyHours
  readonly timeInNorthAmerica?: TimeInNorthAmerica
  readonly firstLanguage?: string
  readonly countryOfOrigin?: string
  readonly industries?: readonly Industry[]

  // Personal goals
  readonly learningGoals?: readonly LearningGoal[]

  // English abilities
  readonly englishAbility?: EnglishAbility
}

// Ephemeral - UI only for now
export type OnboardingResult = {
  readonly proficiencyLevel: string
  readonly personalizedIndustries: readonly Industry[]
  readonly groupClassesPerMonth: number
}

// Backend API types for onboarding
export type OnboardingSubmissionRequest = {
  weekly_hours: WeeklyHours
  first_language: string
  country_of_origin: string
  industry_interests: string[]
  learning_goals: LearningGoal[]
  english_ability: EnglishAbility
}

export type OnboardingResponse = {
  user_id: string
  weekly_hours: WeeklyHours | null
  first_language: string | null
  country_of_origin: string | null
  industry_interests: string[]
  learning_goals: LearningGoal[]
  english_ability: EnglishAbility | null
  completed_at: string | null
  onboarding_version: number
  created_at: string
  updated_at: string
}

// ===== NEW NEEDS ANALYSIS TYPES =====

// Self-assessed English level (A0-C1, no C2)
export type SelfAssessedLevel = 'A0' | 'A1' | 'A2' | 'B1' | 'B2' | 'C1'

// Weekly time commitment options
export type WeeklyTimeCommitment = 'Less than 1 hour' | '1-2 hours' | '2-3 hours' | 'More than 3 hours'

// Primary learning goals (user can select up to 2)
export type PrimaryGoal =
  | 'workplace_communication'
  | 'new_job'
  | 'education_program'
  | 'test_prep'
  | 'community_interaction'

// Industry interests (for career-focused goals)
export type IndustryInterest =
  | 'healthcare'
  | 'business'
  | 'early_childhood_education'
  | 'engineering'
  | 'oil_gas'
  | 'food_service'
  | 'hospitality'
  | 'law_policy'
  | 'retail'
  | 'customer_service'
  | 'construction'
  | 'automotive'
  | 'aviation'
  | 'maritime'
  | 'technology'
  | 'manufacturing'
  | 'animal_care'

// Test types (for test_prep goal)
export type TestType = 'toefl' | 'ielts' | 'casas' | 'pte'

// Communication channel preferences (multi-select, ENG-3474)
export type CommunicationChannelPreference = 'email' | 'sms' | 'phone_call' | 'whatsapp' | 'other'

// Communication frequency preference (single select, ENG-3474)
export type CommunicationFrequencyPreference = 'less_than_weekly' | 'weekly' | 'multiple_per_week' | 'daily'

// Backend API types for needs analysis
export type NeedsAnalysisSubmissionRequest = {
  native_language?: string
  self_assessed_english_level?: SelfAssessedLevel
  weekly_time_commitment: string
  primary_goals: PrimaryGoal[]
  goal_specification?: string // Can be industry or test type
  industry_interests?: string[]
  communication_channels?: CommunicationChannelPreference[]
  communication_frequency?: CommunicationFrequencyPreference
}

export type NeedsAnalysisResponse = {
  user_id: string
  self_assessed_english_level?: SelfAssessedLevel
  weekly_time_commitment: string
  primary_goals: string[]
  goal_specification?: string
  industry_interests?: string[]
  communication_channels?: string[] | null
  communication_frequency?: string | null
  completed_at?: string
  created_at: string
  updated_at: string
}

export type NeedsAnalysisContextResponse = {
  needs_analysis: NeedsAnalysisResponse | null
  has_proficiency_assessment: boolean
}

// UI-only state for the needs analysis survey
export type NeedsAnalysisData = {
  nativeLanguage?: NativeLanguage
  selfAssessedLevel?: SelfAssessedLevel
  weeklyTimeCommitment?: WeeklyTimeCommitment
  primaryGoals: PrimaryGoal[]
  industryInterests: IndustryInterest[]
  testType?: TestType
}

// Display labels for primary goals
export const PRIMARY_GOAL_LABELS: Record<PrimaryGoal, string> = {
  workplace_communication: 'Communicate better at my workplace',
  new_job: 'Prepare for a new job',
  education_program: 'Begin an education or training program',
  test_prep: 'Prepare for a test',
  community_interaction: 'Interact in my community',
}

// Display labels for industries
export const INDUSTRY_LABELS: Record<IndustryInterest, string> = {
  healthcare: 'Healthcare',
  business: 'Business/Management/Finance',
  early_childhood_education: 'Early Childhood Education',
  engineering: 'Engineering',
  oil_gas: 'Oil & Gas',
  food_service: 'Food Service',
  hospitality: 'Hospitality',
  law_policy: 'Law & Policy',
  retail: 'Retail',
  customer_service: 'Customer Service',
  construction: 'Construction',
  automotive: 'Automotive',
  aviation: 'Aviation',
  maritime: 'Maritime',
  technology: 'Technology',
  manufacturing: 'Manufacturing',
  animal_care: 'Animal Care',
}

// Display labels for test types
export const TEST_TYPE_LABELS: Record<TestType, string> = {
  toefl: 'TOEFL (Test of English as a Foreign Language)',
  ielts: 'IELTS (International English Language Testing System)',
  casas: 'CASAS (Comprehensive Adult Student Assessment Systems)',
  pte: 'PTE (Pearson Test of English)',
}

// Display labels for communication channel preferences
export const COMMUNICATION_CHANNEL_LABELS: Record<CommunicationChannelPreference, string> = {
  email: 'Email',
  sms: 'SMS',
  phone_call: 'Phone call',
  whatsapp: 'WhatsApp',
  other: 'Other',
}

// Display labels for communication frequency preferences
export const COMMUNICATION_FREQUENCY_LABELS: Record<CommunicationFrequencyPreference, string> = {
  less_than_weekly: 'Less than once a week',
  weekly: 'Once a week',
  multiple_per_week: 'Multiple times a week',
  daily: 'Daily',
}

// English ability descriptions for Q2
export const ENGLISH_ABILITY_DESCRIPTIONS: Record<SelfAssessedLevel, string> = {
  A0: 'Do very little or nothing',
  A1: 'Say a few words. Read signs',
  A2: 'Tell a simple story. Understand a simple conversation',
  B1: 'Read and understand the news. Discuss topics with my friends and colleagues',
  B2: 'Understand most things I read and have conversations on many topics, but I still make mistakes',
  C1: 'Do almost everything, but I am not perfect and would benefit from continued practice and expert guidance',
}
